/**
 * Money → Revenue vs usage — prototype VIEWS.admin + quadSvg (docs/prototype.html L4379–4419). GET /money/overview:
 * the four KPI tiles, the usage-against-revenue quadrant (or table), Revenue at risk, Ops effort against revenue and
 * Revenue concentration. The freshness chip says where the money figures came from (decision 16: per-product MRR
 * from Settings until Zoho first syncs, then Zoho only) and a stale or failed sync shows a banner.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EFFORT_FLAG_LABEL, MONEY_ZONE_INFO, MONEY_ZONE_LINES, MONEY_ZONES, type MoneyClientRow, type MoneyZone } from '@amplispot/contracts';
import { inr, syncedAt, useMoneyOverview } from '../../lib/money';
import { offScaleMarkBox, placeLabels, quadrantTitles, revenueScale } from '../../lib/quad';
import { ErrorState, Loading, Seg } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

export function FreshnessChip({ f }: { f: { source: 'manual' | 'zoho'; last_success_at: string | null; stale: boolean } }) {
  if (f.source === 'zoho' && f.last_success_at) return <span className={`chip ${f.stale ? 'warm' : 'brand'}`}>Zoho synced {syncedAt(f.last_success_at)}</span>;
  return <span className="chip warm">Zoho not connected: figures from Settings</span>;
}

export function MoneyTop({ f }: { f: { source: 'manual' | 'zoho'; last_success_at: string | null; stale: boolean; banner: string | null } }) {
  return (
    <>
      <div className="bar" style={{ justifyContent: 'flex-end', marginTop: -6 }}>
        <FreshnessChip f={f} />
      </div>
      {f.stale && f.banner ? (
        <div className="note warn" style={{ marginBottom: 14 }}>
          {f.banner}
        </div>
      ) : null}
    </>
  );
}

export function RevenueUsage() {
  const q = useMoneyOverview();
  const nav = useNavigate();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  if (q.isPending) return <div className="panel"><Loading /></div>;
  if (q.isError) return <div className="panel"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { kpis: k, clients, freshness, labels } = q.data;
  const open = (id: string) => nav(`/clients/book?client=${id}`);
  const plotted = clients.filter((c) => c.zone);
  const noUse = clients.filter((c) => !c.zone);
  const risk = clients.filter((c) => c.zone === 'revenue_at_risk').sort((a, b) => b.mrr_lakh - a.mrr_lakh);

  return (
    <>
      <MoneyTop f={freshness} />
      <div className="stats">
        <Stat k="Monthly revenue" v={inr(k.total_mrr_lakh)} s={labels.monthly_revenue} />
        <Stat k="Revenue at risk" v={inr(k.revenue_at_risk_lakh)} s={`${Math.round(k.revenue_at_risk_pct)}% of total. ${labels.revenue_at_risk}`} cls="red" />
        <Stat k="Top 3 clients" v={`${Math.round(k.top3_share_pct)}%`} s="of revenue" cls={k.top3_amber ? 'amber' : ''} />
        <Stat k="Ops hours per ₹ lakh" v={k.ops_hours_per_lakh === null ? 'No data' : k.ops_hours_per_lakh.toFixed(1)} s={`${Math.round(k.total_hours)} hours this month`} />
      </div>
      <div className="cols c21">
        <div className="panel">
          <div className="hd">
            <h3>Usage against revenue</h3>
            <div className="r">
              <Seg
                options={[
                  ['chart', 'Chart'],
                  ['table', 'Table'],
                ]}
                value={view}
                onChange={setView}
              />
            </div>
          </div>
          <div className="bd">
            {view === 'chart' ? (
              <>
                <Quad rows={plotted} onOpen={open} />
                <div className="split" style={{ flexWrap: 'wrap', gap: 16, marginTop: 8, fontSize: 12.5 }}>
                  {MONEY_ZONES.map((z) => (
                    <span key={z} className="split" style={{ gap: 6 }}>
                      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                        <ZoneShape z={z} x={9} y={9} plain />
                      </svg>
                      {MONEY_ZONE_INFO[z].label}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="tr">Plan used</th>
                    <th className="tr">Revenue / month</th>
                    <th>Zone</th>
                  </tr>
                </thead>
                <tbody>
                  {plotted.map((c) => (
                    <tr key={c.client.id} className="row" onClick={() => open(c.client.id)}>
                      <td>{c.client.name}</td>
                      <td className="tr num">{Math.round(c.weighted_util!)}%</td>
                      <td className="tr num">{inr(c.mrr_lakh)}</td>
                      <td>{MONEY_ZONE_INFO[c.zone!].label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {noUse.length ? (
              <div className="note warn" style={{ marginTop: 10 }}>
                {/* Prototype lists every name; a real book can miss usage for dozens, so name five and count the rest
                    (the Table view lists only plotted clients; each name opens from Clients). */}
                {noUse.slice(0, 5).map((c) => c.client.name).join(', ')}
                {noUse.length > 5 ? ` and ${noUse.length - 5} more` : ''} not plotted: usage data is missing.
              </div>
            ) : null}
            {!clients.length ? <div className="empty">No clients yet</div> : null}
          </div>
        </div>
        <div className="panel" style={{ alignSelf: 'start' }}>
          <div className="hd">
            <h3>Revenue at risk</h3>
            <div className="r">High revenue, low use</div>
          </div>
          {risk.length ? (
            risk.map((c) => (
              <div key={c.client.id} className="li click" onClick={() => open(c.client.id)}>
                <span className="dot cold" style={{ marginTop: 6 }} />
                <div className="b">
                  <div className="t">{c.client.name}</div>
                  <div className="m">
                    <span>{Math.round(c.weighted_util!)}% used</span>
                    {c.trend !== null ? <span>{Math.round(c.trend)}% vs baseline</span> : null}
                    {c.open_escalations ? <span className="chip cold">Escalated</span> : null}
                  </div>
                </div>
                <b className="num">{inr(c.mrr_lakh)}</b>
              </div>
            ))
          ) : (
            <div className="empty">None</div>
          )}
        </div>
      </div>
      <div className="cols c2 mt">
        <div className="panel">
          <div className="hd">
            <h3>Ops effort against revenue</h3>
            <div className="r">Share of total</div>
          </div>
          <div className="bd" style={{ display: 'grid', gap: 12 }}>
            {clients.map((c) => {
              const rs = Math.round(c.revenue_share_pct);
              const hs = Math.round(c.hours_share_pct);
              return (
                <div key={c.client.id}>
                  <div className="split" style={{ fontSize: 13 }}>
                    <b>{c.client.name}</b>
                    <div className="grow" />
                    {c.effort_flag ? <span className={`chip ${c.effort_flag === 'more_effort' ? 'warm' : 'info'}`}>{EFFORT_FLAG_LABEL[c.effort_flag]}</span> : null}
                  </div>
                  <Bar label="Revenue" pct={rs} />
                  <Bar label="Hours" pct={hs} warm />
                </div>
              );
            })}
          </div>
        </div>
        <div className="panel">
          <div className="hd">
            <h3>Revenue concentration</h3>
            <div className="r">Largest first</div>
          </div>
          <div className="bd" style={{ display: 'grid', gap: 9 }}>
            {clients.map((c) => (
              <div key={c.client.id} className="split" style={{ gap: 10 }}>
                <span style={{ width: 150, fontSize: 13 }}>{c.client.name}</span>
                <div className="meter grow" title={`${c.client.name}: ${inr(c.mrr_lakh)}`}>
                  <i style={{ width: `${Math.min(100, c.revenue_share_pct * 4)}%` }} />
                </div>
                <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 12 }}>
                  {Math.round(c.revenue_share_pct)}%
                </span>
                <span className="num" style={{ width: 44, textAlign: 'right', fontSize: 12, color: 'var(--mute)' }}>
                  {Math.round(c.cumulative_share_pct)}%
                </span>
              </div>
            ))}
            <div style={{ fontSize: 11.5, color: 'var(--mute)', textAlign: 'right' }}>Share, then running total</div>
          </div>
        </div>
      </div>
    </>
  );
}

function Bar({ label, pct, warm }: { label: string; pct: number; warm?: boolean }) {
  return (
    <div className="split" style={{ marginTop: warm ? 3 : 4, gap: 8 }}>
      <span style={{ width: 56, fontSize: 11, color: 'var(--sub)' }}>{label}</span>
      <div className={`meter grow${warm ? ' warm' : ''}`}>
        <i style={{ width: `${Math.min(100, pct * 3)}%` }} />
      </div>
      <span className="num" style={{ width: 34, textAlign: 'right', fontSize: 12 }}>
        {pct}%
      </span>
    </div>
  );
}

/** Prototype zoneShape: circle core, triangle risk, diamond grow, square watch. */
function ZoneShape({ z, x, y, plain }: { z: MoneyZone; x: number; y: number; plain?: boolean }) {
  const r = 9;
  const col = MONEY_ZONE_INFO[z].colour;
  const st = plain ? {} : { stroke: '#fff', strokeWidth: 2 };
  if (z === 'revenue_at_risk') return <path d={`M${x},${y - r - 1} L${x + r + 1},${y + r} L${x - r - 1},${y + r} Z`} fill={col} {...st} />;
  if (z === 'room_to_grow') return <path d={`M${x},${y - r - 2} L${x + r + 2},${y} L${x},${y + r + 2} L${x - r - 2},${y} Z`} fill={col} {...st} />;
  if (z === 'keep_an_eye_on') return <rect x={x - r + 1} y={y - r + 1} width={2 * r - 2} height={2 * r - 2} rx={2} fill={col} {...st} />;
  return <circle cx={x} cy={y} r={r} fill={col} {...st} />;
}

/** Prototype quadSvg: share of plan used (x, 0–100%) against monthly revenue (y, ₹ lakh), zone lines dashed. */
function Quad({ rows, onOpen }: { rows: MoneyClientRow[]; onOpen: (id: string) => void }) {
  const W = 680, H = 380, L = 54, R = 20, T = 16, B = 44;
  const sc = revenueScale(rows.map((r) => r.mrr_lakh), T, H - B);
  const py = sc.py;
  const px = (u: number) => L + (Math.min(100, u) / 100) * (W - L - R);
  const { util_pct, mrr_lakh } = MONEY_ZONE_LINES;
  const pts = rows.map((c) => {
    const name = c.client.name.split(' ')[0] ?? c.client.name;
    return { id: c.client.id, x: px(c.weighted_util!), y: py(c.mrr_lakh), text: sc.isOff(c.mrr_lakh) ? `${name} ${inr(c.mrr_lakh)}` : name };
  });
  // The quadrant titles are fixed text, and an off-scale point's caret is drawn above its marker: labels keep clear of both.
  const qt = quadrantTitles(px, T, W - R);
  const carets = pts.filter((_, i) => sc.isOff(rows[i]!.mrr_lakh)).map((p) => ({ ...offScaleMarkBox(p.x, p.y), h: 5 }));
  const labels = placeLabels(pts, { x: L, y: T, w: W - L - R + 16, h: H - B - T + 4 }, [qt.risk, qt.healthy, ...carets]);
  const fmt = (m: number) => (Number.isInteger(m) ? m : m.toFixed(1));
  return (
    <svg className="quad" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Utilisation against monthly revenue for each client">
      <rect x={px(0)} y={T} width={px(util_pct) - px(0)} height={py(mrr_lakh) - T} fill="#FDE9EE" opacity={0.5} />
      {sc.ticks.map((m) => (
        <g key={m}>
          <line x1={L} x2={W - R} y1={py(m)} y2={py(m)} stroke="#E4E8F1" />
          <text x={L - 8} y={py(m) + 4} textAnchor="end" fontSize="11" fill="#69718A" fontFamily="JetBrains Mono">
            {fmt(m)}
          </text>
        </g>
      ))}
      {[0, 20, 40, 60, 80, 100].map((u) => (
        <text key={u} x={px(u)} y={H - B + 18} textAnchor="middle" fontSize="11" fill="#69718A" fontFamily="JetBrains Mono">
          {u}%
        </text>
      ))}
      <line x1={px(util_pct)} x2={px(util_pct)} y1={T} y2={H - B} stroke="#9AA1B5" strokeDasharray="4 4" />
      <line x1={L} x2={W - R} y1={py(mrr_lakh)} y2={py(mrr_lakh)} stroke="#9AA1B5" strokeDasharray="4 4" />
      <text x={(L + W - R) / 2} y={H - 6} textAnchor="middle" fontSize="12" fill="#69718A">
        Share of plan used
      </text>
      <text transform={`translate(14 ${(T + H - B) / 2}) rotate(-90)`} textAnchor="middle" fontSize="12" fill="#69718A">
        Monthly revenue, ₹ lakh
      </text>
      <text x={qt.risk.x} y={qt.risk.baseline} fontSize="11" fontWeight="600" fill="#BE123C">
        Revenue at risk
      </text>
      <text x={qt.healthy.endX} y={qt.healthy.baseline} textAnchor="end" fontSize="11" fontWeight="600" fill="#047857">
        Healthy core
      </text>
      {rows.map((c, i) => {
        const { x, y, text } = pts[i]!;
        const lb = labels.get(c.client.id)!;
        return (
          <g key={c.client.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(c.client.id)}>
            <title>{`${c.client.name}\n${Math.round(c.weighted_util!)}% of plan used\n${inr(c.mrr_lakh)} / month\n${MONEY_ZONE_INFO[c.zone!].label}`}</title>
            <ZoneShape z={c.zone!} x={x} y={y} />
            {sc.isOff(c.mrr_lakh) ? (
              <path data-testid="quad-off-scale" d={`M${x - 4},${y - 10} L${x},${y - 14} L${x + 4},${y - 10}`} stroke="#69718A" strokeWidth="1.4" fill="none" aria-hidden="true" />
            ) : null}
            <text x={lb.x} y={lb.y} textAnchor={lb.anchor} fontSize="11" fill="#10131F" stroke="#fff" strokeWidth={3} paintOrder="stroke" strokeLinejoin="round">
              {text}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
